import { supabase } from "./supabase";

/**
 * Per-school provider credentials helper.
 *
 * Used by the CMS (SchoolDetail → Providers tab) to read/write rows in the
 * `school_provider_credentials` table. The backend reads the same rows with
 * the service role key when issuing SSO tokens — see
 * Backend/src/providers/highlights-library/index.js.
 */

const TABLE = "school_provider_credentials";

/**
 * Get the saved credentials (raw settings jsonb) for a school + provider.
 * Returns {} when nothing is saved yet.
 */
export async function getSchoolProviderCredentials(schoolId, providerKey) {
  const { data, error } = await supabase
    .from(TABLE)
    .select("settings, updated_at")
    .eq("school_id", schoolId)
    .eq("provider_key", providerKey)
    .maybeSingle();

  if (error) throw error;
  if (!data) return { settings: {}, updated_at: null };
  return { settings: data.settings || {}, updated_at: data.updated_at };
}

/**
 * Upsert per-school provider credentials.
 * `settings` is the provider-specific jsonb payload.
 * For highlights-library this is `{ company_code, sso_code }` — the LMS ID
 * and AES key issued by Highlights per registered school.
 */
export async function saveSchoolProviderCredentials(
  schoolId,
  providerKey,
  settings,
) {
  const { data, error } = await supabase
    .from(TABLE)
    .upsert(
      {
        school_id: schoolId,
        provider_key: providerKey,
        settings,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "school_id,provider_key" },
    )
    .select("id, updated_at")
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete per-school provider credentials (revert to env fallback for that
 * provider, if any).
 */
export async function clearSchoolProviderCredentials(schoolId, providerKey) {
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq("school_id", schoolId)
    .eq("provider_key", providerKey);

  if (error) throw error;
  return true;
}

/**
 * Hit the backend verify endpoint to confirm the saved credentials resolve
 * and decode correctly. Returns a diagnostic object — never the secret.
 */
export async function verifySchoolHighlightsCredentials(schoolId) {
  const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!adminApiKey) throw new Error("REACT_APP_ADMIN_API_KEY not configured");
  if (!apiBaseUrl) throw new Error("REACT_APP_API_BASE_URL is not configured");

  const normalizedBaseUrl = apiBaseUrl.replace(/\/$/, "").replace(/\/api$/, "");
  const url = `${normalizedBaseUrl}/api/providers/highlights-library/school/${encodeURIComponent(
    schoolId,
  )}/verify-config`;

  const response = await fetch(url, {
    method: "GET",
    headers: { "x-api-key": adminApiKey },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      data.message || data.error || `Verify failed (HTTP ${response.status})`;
    const err = new Error(message);
    err.payload = data;
    throw err;
  }
  return data;
}
